// Cashier-machine Firebase Auth (custom token) — RTDB security rollout.
//
// Named useCashierAuth to avoid colliding with useFirebaseAuth.ts (the
// owner-report Google sign-in). Both use the DEFAULT Firebase app's auth —
// by design there is no second app/database instance: owner pages and POS
// pages run in separate contexts, so the single auth slot is fine.
//
// Lifecycle: sign-in happens ONCE per machine. The custom token from the
// backend is single-use; afterwards the Firebase SDK persists a non-expiring
// refresh token in IndexedDB and silently renews the 1-hour ID token — across
// browser restarts and offline periods. A machine only needs a new custom
// token after signOut, backend revocation, user deletion, or cleared site data.
//
// The ONE hard check (per product decision): on a cashier machine, if the
// session's custom claim customerId does not match the shop currently logged in,
// the machine must be bounced out (bootstrap redirects to /login). Everything
// else passes through.
//
// Rollout flag: while the backend endpoint does not exist yet (Phase 1 not
// deployed server-side), STRICT_CASHIER_AUTH must stay false so cashier
// machines boot fail-soft instead of bouncing every shop to /login.
// Flip to true once POST /users/firebase-token is live.

import { signInWithCustomToken, signOut, getIdTokenResult } from "firebase/auth";
import { auth } from "~/firebaseInstance";

export const STRICT_CASHIER_AUTH = true;

const FIREBASE_TOKEN_PATH = "/users/firebase-token";
const RETRIES = 2;
const RETRY_DELAY_MS = 2000;

export type CashierAuthResult =
  | { status: "ok"; uid: string }
  | { status: "mismatch"; uid: string } // claim customerId ≠ current shop → bounce out
  | { status: "unauthorized" } // backend rejected the POS token → need /login
  | { status: "unreachable" }; // network/endpoint failure with no cached session

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Compare the session's custom claims against the shop we are logged into.
 * Returns "match", "mismatch", or "unknown" (claims unreadable — e.g. token
 * expired while offline; let it pass, the security rules are the backstop).
 */
async function checkClaims(customerId: string): Promise<"match" | "mismatch" | "unknown"> {
  const user = auth.currentUser;
  if (!user) return "unknown";
  try {
    const { claims } = await getIdTokenResult(user);
    return claims.cashier === true && claims.customerId === customerId ? "match" : "mismatch";
  } catch (err) {
    console.warn("[cashier-auth] cannot read claims (offline?) — passing through", err);
    return "unknown";
  }
}

async function mintAndSignIn(): Promise<CashierAuthResult> {
  const { $api } = useApi();
  const authStore = useAuthStore();
  let lastError: unknown = null;

  for (let attempt = 0; attempt <= RETRIES; attempt++) {
    try {
      const data = await $api<{ firebaseCustomToken: string }>(FIREBASE_TOKEN_PATH, {
        method: "POST",
        body: { browserId: authStore.browserId },
      });
      const cred = await signInWithCustomToken(auth, data.firebaseCustomToken);
      console.log(`[cashier-auth] signed in as ${cred.user.uid}`);
      return { status: "ok", uid: cred.user.uid };
    } catch (err: any) {
      lastError = err;
      const status = err?.response?.status ?? err?.statusCode ?? err?.status;
      if (status === 401 || status === 403) return { status: "unauthorized" };
      if (attempt < RETRIES) await delay(RETRY_DELAY_MS * (attempt + 1));
    }
  }
  console.warn("[cashier-auth] could not reach firebase-token endpoint", lastError);
  return { status: "unreachable" };
}

/**
 * Ensure this cashier machine holds a Firebase session whose custom claims
 * match the current shop (jobchain is cashier-only once the security rules
 * ship).
 *
 * A session already persisted in IndexedDB wins when its claims match —
 * including offline, where claims may be unreadable and we pass through (the
 * server-side rules are the backstop). A session for the WRONG shop — or a
 * non-cashier session such as an owner Google sign-in left on this machine —
 * is signed out and replaced by a freshly minted token for the current shop;
 * if the replacement still mismatches, "mismatch" is returned and the caller
 * bounces the machine out.
 */
export async function ensureCashierFirebaseAuth(customerId: string): Promise<CashierAuthResult> {
  await auth.authStateReady();

  if (auth.currentUser) {
    const verdict = await checkClaims(customerId);
    if (verdict !== "mismatch") {
      return { status: "ok", uid: auth.currentUser.uid };
    }
    if (!STRICT_CASHIER_AUTH) {
      // Phase 1: no custom tokens exist yet, so a "mismatch" here is some other
      // session (e.g. owner Google). Leave it untouched and pass through.
      console.warn("[cashier-auth] claims mismatch ignored (non-strict rollout phase)");
      return { status: "ok", uid: auth.currentUser.uid };
    }
    console.warn("[cashier-auth] session claims do not match current shop — re-minting");
    await signOutCashierFirebaseAuth();
  }

  const result = await mintAndSignIn();
  if (result.status !== "ok") return result;

  const verdict = await checkClaims(customerId);
  if (verdict === "mismatch") return { status: "mismatch", uid: result.uid };
  return result;
}

export async function signOutCashierFirebaseAuth() {
  try {
    await signOut(auth);
  } catch (err) {
    console.warn("[cashier-auth] signOut failed", err);
  }
}
